"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeCSVMapper = exports.DefaultKeyMappings = void 0;
const core_api_1 = require("@ikegps/core-api");
const index_1 = require("../../index");
const location_field_1 = require("../fields/location.field");
const nested_list_field_1 = require("../fields/nested.list.field");
const MappingFunctions = {
    // [IkeFieldType.NUMBER]: (field: CollectionModelFieldNumber, val: string) => field.Mutator.manual(val),
    [core_api_1.IkeFieldType.TEXT]: (field, val) => {
        if (val == null || val === '') {
            return true;
        }
        field.Mutator.manual(String(val));
        return true;
    },
    [core_api_1.IkeFieldType.SELECT_LIST]: (field, valIn) => {
        if (valIn == null || valIn === '') {
            return true;
        }
        const val = String(valIn);
        field.Mutator.manual(val);
        const hasValue = field.hasValue();
        if (hasValue) {
            return true;
        }
        // Attempt to match based on the schema options
        const textMatch = field.schema.options.find((opt) => opt.text.toLowerCase() === val.toLowerCase());
        if (textMatch) {
            field.Mutator.manual(textMatch.value);
        }
        return field.hasValue();
    },
    [core_api_1.IkeFieldType.NESTED_LIST]: (field, val) => {
        if (val == null || val === '') {
            return true;
        }
        field.Mutator.manual(nested_list_field_1.CollectionModelFieldNestedList.parse(val));
        return field.hasValue();
    },
    [core_api_1.IkeFieldType.LOCATION]: (field, val, key) => {
        const oldValue = Object.assign({}, field.value);
        const value = parseFloat(val);
        if (isNaN(value) || isNaN(val)) {
            return false;
        }
        switch (key) {
            case 'lat':
                oldValue.latitude = parseFloat(val);
                field.Mutator.manual(oldValue);
                return field.value.latitude === oldValue.latitude;
            case 'lng':
                oldValue.longitude = parseFloat(val);
                field.Mutator.manual(oldValue);
                return field.value.longitude === oldValue.longitude;
            default:
                throw new Error('Invalid location key: "' + key + "'");
        }
    },
};
/**
 * Lower cased alias's for the keys in the mapping process.
 */
exports.DefaultKeyMappings = {
    lat: ['lat', 'latitude'],
    lng: ['lng', 'longitude', 'long'],
};
class IkeCSVMapper {
    constructor(csv, form) {
        this.csv = null;
        this.form = null;
        /** List of fields that can be mapped to */
        this.mappings = [];
        /** List of the collections that have been created by these mappings */
        this.processed = [];
        this.csv = csv;
        this.form = form;
        for (const field of this.form.fields) {
            if (field.type === 'location') {
                this.mappings.push({ field, fieldKey: 'lat', csvIndex: -1, isValid: true });
                this.mappings.push({ field, fieldKey: 'lng', csvIndex: -1, isValid: true });
            }
            else if (MappingFunctions[field.type]) {
                this.mappings.push({ field, csvIndex: -1, isValid: true });
            }
        }
    }
    toJSON() {
        const output = [];
        for (const mapping of this.mappings) {
            if (mapping.csvIndex === -1) {
                continue;
            }
            output.push({
                fieldId: mapping.field.id,
                fieldKey: mapping.fieldKey,
                csvHeader: this.csv.headers[mapping.csvIndex],
            });
        }
        return output;
    }
    getMapping(fieldId, fieldKey) {
        for (const mapping of this.mappings) {
            if (mapping.field.id !== fieldId) {
                continue;
            }
            if (fieldKey == null || fieldKey === mapping.fieldKey) {
                return mapping;
            }
        }
        return null;
    }
    setMapping(mapping, csvHeaderIndex, shouldProcess = true) {
        if (csvHeaderIndex == null || isNaN(csvHeaderIndex) || csvHeaderIndex < 0) {
            mapping.csvIndex = -1;
        }
        else {
            mapping.csvIndex = csvHeaderIndex;
        }
        if (shouldProcess) {
            this.processRows();
        }
    }
    getCollectionValue(mapping, index) {
        const collection = this.processed[index];
        const raw = this.csv.rows[index];
        const rawValue = raw[mapping.csvIndex];
        const field = collection.getField(mapping.field.id);
        if (location_field_1.CollectionModelFieldLocation.isLocationField(field)) {
            let value = 0;
            switch (mapping.fieldKey) {
                case 'lat':
                    value = field.value.latitude;
                    break;
                case 'lng':
                    value = field.value.longitude;
                    break;
                default:
                    throw new Error('Invalid mapping key');
            }
            return { value: String(value), isValid: value !== 0, raw: rawValue };
        }
        const hasRawValue = rawValue != null && rawValue !== '';
        if (hasRawValue) {
            return { value: field.displayValue, isValid: field.hasValue(), raw: rawValue };
        }
        else {
            return { value: field.displayValue, isValid: true, raw: rawValue };
        }
    }
    reset() {
        for (const mapping of this.mappings) {
            mapping.csvIndex = -1;
            mapping.isValid = true;
        }
    }
    /**
     * Attempt to guess some default mappings based off the headers in the CSV and the fields in the form
     */
    setDefaultMappings() {
        const headerSearch = {};
        for (let i = 0; i < this.csv.headers.length; i++) {
            const header = this.csv.headers[i];
            headerSearch[header.toLowerCase()] = i;
        }
        let hasMapped = false;
        const applyMapping = (key, mapping) => {
            if (mapping.csvIndex > -1) {
                return true;
            }
            const index = headerSearch[key];
            if (index != null) {
                delete headerSearch[key];
                this.setMapping(mapping, index, false);
                hasMapped = true;
                return true;
            }
            return false;
        };
        for (const mapping of this.mappings) {
            const { field, fieldKey } = mapping;
            // Is there a direct match between header name and field name
            if (applyMapping(field.displayName.toLowerCase(), mapping)) {
                continue;
            }
            // See if any other keys will map
            const searchKeys = exports.DefaultKeyMappings[fieldKey];
            if (searchKeys == null) {
                continue;
            }
            for (const search of searchKeys) {
                if (applyMapping(search, mapping)) {
                    break;
                }
            }
        }
        if (hasMapped) {
            this.processRows();
        }
    }
    get isValid() {
        if (this.csv.rows.length === 0) {
            return false;
        }
        let hasMappings = false;
        for (const field of this.mappings) {
            if (field.csvIndex > -1) {
                hasMappings = true;
            }
            if (field.isValid === false) {
                return false;
            }
        }
        return hasMappings;
    }
    processRows() {
        this.processed = [];
        for (const field of this.mappings) {
            field.isValid = true;
        }
        for (const row of this.csv.rows) {
            this.processed.push(this.processRow(row));
        }
    }
    processRow(row) {
        const collection = index_1.IkeModel.Collection.create(this.form, null); // TODO do we always need a loader?
        for (const mapping of this.mappings) {
            if (mapping.csvIndex === -1) {
                continue;
            }
            const field = collection.getField(mapping.field.id);
            const Mapper = MappingFunctions[mapping.field.type];
            const isValid = Mapper(field, row[mapping.csvIndex], mapping.fieldKey);
            if (isValid === false) {
                mapping.isValid = false;
            }
        }
        return collection;
    }
}
exports.IkeCSVMapper = IkeCSVMapper;
//# sourceMappingURL=csv.mapper.js.map